import React, { useState, useMemo, useEffect } from 'react';
import { usePermission } from '../../../src/utils/permissions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Separator } from '../../ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { Progress } from '../../ui/progress';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../ui/dropdown-menu';
import {
  Receipt,
  Plus,
  Search,
  Eye,
  Download,
  TrendingUp,
  TrendingDown,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Users,
  MapPin,
  Save,
  Edit,
  Upload,
  Trash2,
  MoreVertical,
  Paperclip,
  RefreshCw,
  FileText
} from 'lucide-react';
import { financialService, BudgetChangeOrder, BudgetChangeOrderCreateData } from '../../../src/services/financial.service';
import { showSuccess, showError } from '../../../src/utils/notification.utils';
import { useAuth } from '../../../src/hooks/useAuth';
import { useOrgCurrency } from '../../../src/hooks/useOrgCurrency';
import { formatCurrency } from '../../../src/utils/currency';
import { useChangeOrders } from '../../../src/hooks/queries/use-change-orders';
import { useBudgets } from '../../../src/hooks/queries/use-budgets';
import { useContracts } from '../../../src/hooks/queries/use-contracts';
import { useAppNav } from '../../../src/contexts/app-nav.context';

// Change Order Form Component
function ChangeOrderFormModal({ mode, changeOrder, trigger, onSuccess, orgId, availableBudgets = [], availableContracts = [], open: controlledOpen, onOpenChange }: {
  mode: 'add' | 'edit' | 'view';
  changeOrder?: any;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  orgId?: string;
  availableBudgets?: any[];
  availableContracts?: any[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const orgCurrency = useOrgCurrency();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const handleOpenChange = (val: boolean) => {
    if (onOpenChange) onOpenChange(val);
    else setInternalOpen(val);
  };
  const isReadOnly = mode === 'view';
  const [activeTab, setActiveTab] = useState('basic');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    coNumber: changeOrder?.coNumber || '',
    title: changeOrder?.title || '',
    contract: changeOrder?.contract || changeOrder?.budget_id || '',
    contract_id: changeOrder?.contract_id || '',
    project: changeOrder?.project || changeOrder?.project_id || '',
    description: changeOrder?.description || '',
    justification: changeOrder?.justification || '',
    costImpact: changeOrder?.costImpact || '',
    scheduleImpact: changeOrder?.scheduleImpact || '',
    impactType: changeOrder?.impactType || 'Addition',
    priority: changeOrder?.priority || 'Medium',
    category: changeOrder?.category || '',
    initiatedBy: changeOrder?.initiatedBy || '',
    requestDate: changeOrder?.requestDate || new Date().toISOString().split('T')[0],
    targetDate: changeOrder?.targetDate || '',
    estimatedCost: changeOrder?.estimatedCost || '',
    laborHours: changeOrder?.laborHours || '',
    materialCost: changeOrder?.materialCost || '',
    equipmentCost: changeOrder?.equipmentCost || '',
    overheadPercentage: changeOrder?.overheadPercentage || '15',
    riskAssessment: changeOrder?.riskAssessment || 'Low',
    resourceRequirements: changeOrder?.resourceRequirements || '',
    qualityImpact: changeOrder?.qualityImpact || '',
    safetyImpact: changeOrder?.safetyImpact || '',
    environmentalImpact: changeOrder?.environmentalImpact || '',
    stakeholderImpact: changeOrder?.stakeholderImpact || '',
    mitigationPlan: changeOrder?.mitigationPlan || '',
    approvalHistory: changeOrder?.approvalHistory || [
      { id: 1, approver: 'Project Manager', role: 'PM', status: 'Pending', date: '', comments: '', required: true },
      { id: 2, approver: 'Client Representative', role: 'Client', status: 'Pending', date: '', comments: '', required: true },
      { id: 3, approver: 'Engineering Lead', role: 'Engineer', status: 'Pending', date: '', comments: '', required: false }
    ],
    attachments: [] as Array<{ file_id: string; file_path: string }>,
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [uploadedFiles, setUploadedFiles] = useState<Array<{ id: string; name: string; size: number; type: string }>>([]);
  const [isUploading, setIsUploading] = useState(false);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    try {
      const { documentService } = await import('../../../src/services/document.service');
      const uploadPromises = Array.from(files).map(async (file) => {
        const uploadResponse = await documentService.uploadFile(file, orgId || '');
        if (!uploadResponse.success || !uploadResponse.data) {
          throw new Error(`Failed to upload ${file.name}`);
        }
        return {
          id: uploadResponse.data.storage_id,
          name: file.name,
          size: file.size,
          type: file.type,
          filePath: uploadResponse.data.storage_id,
        };
      });
      const newFiles = await Promise.all(uploadPromises);
      setUploadedFiles(prev => [...prev, ...newFiles]);
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newFiles.map(f => ({ file_id: f.id, file_path: f.filePath }))],
      }));
      showSuccess(`${newFiles.length} file(s) uploaded successfully`);
    } catch (error: any) {
      showError({ title: 'Upload Failed', message: error.message || 'Failed to upload files. Please try again.' });
    } finally {
      setIsUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.file_id !== fileId),
    }));
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.costImpact || parseFloat(formData.costImpact) === 0) {
      newErrors.costImpact = 'Cost impact is required';
    }
    if (!formData.contract) {
      newErrors.contract = 'Linked budget is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Maps the form's free-text category labels to the API's ChangeOrderType enum.
  const categoryToChangeType = (cat: string): import('../../../src/services/financial.service').ChangeOrderType | undefined => {
    const map: Record<string, import('../../../src/services/financial.service').ChangeOrderType> = {
      'Scope Addition': 'scope_addition',
      'Scope Deletion': 'scope_reduction',
      'Scope Modification': 'scope_reduction',
      'Design Change': 'design_change',
      'Specification Change': 'design_change',
      'Regulatory Requirement': 'other',
      'Client Request': 'owner_request',
      'Safety Enhancement': 'other',
      'Quality Improvement': 'other',
      'Cost Optimization': 'other',
    };
    return cat ? (map[cat] ?? 'other') : undefined;
  };

  // Maps the form's impact type labels to the API's CostImpactType enum.
  const impactLabelToType = (label: string): import('../../../src/services/financial.service').CostImpactType | undefined => {
    const map: Record<string, import('../../../src/services/financial.service').CostImpactType> = {
      'Addition': 'cost_increase',
      'Enhancement': 'cost_increase',
      'Modification': 'cost_increase',
      'Deletion': 'cost_decrease',
      'Substitution': 'no_cost_impact',
    };
    return label ? (map[label] ?? undefined) : undefined;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      setActiveTab('basic');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload: BudgetChangeOrderCreateData = {
        organization_id: orgId || '',
        budget_id: formData.contract || '',
        project_id: formData.project || undefined,
        change_order_number: formData.coNumber || `CO-${Date.now()}`,
        title: formData.title,
        description: formData.description || undefined,
        change_type: categoryToChangeType(formData.category),
        impact_type: impactLabelToType(formData.impactType),
        cost_impact: parseFloat(formData.costImpact) || 0,
        schedule_impact_days: parseInt(formData.scheduleImpact) || undefined,
        request_date: formData.requestDate || undefined,
        required_decision_date: formData.targetDate || undefined,
        reason_for_change: formData.justification || undefined,
        documents: formData.attachments.length > 0 ? formData.attachments : undefined,
      };
      if (changeOrder?.change_order_id) {
        await financialService.updateBudgetChangeOrder(changeOrder.change_order_id, payload);
        showSuccess('Change order updated');
      } else {
        await financialService.createBudgetChangeOrder(payload);
        showSuccess('Change order created');
      }
      handleOpenChange(false);
      onSuccess?.();
    } catch {
      showError('Failed to save change order');
    } finally {
      setIsSubmitting(false);
    }
  };


  const categories = [
    'Scope Addition',
    'Scope Deletion',
    'Scope Modification',
    'Design Change',
    'Specification Change',
    'Regulatory Requirement',
    'Client Request',
    'Safety Enhancement',
    'Quality Improvement',
    'Cost Optimization'
  ];

  const impactTypes = [
    'Addition',
    'Deletion',
    'Modification',
    'Substitution',
    'Enhancement'
  ];

  const updateApprovalHistory = (id: number, field: string, value: string | boolean) => {
    setFormData({
      ...formData,
      approvalHistory: formData.approvalHistory.map(approval => 
        approval.id === id ? { ...approval, [field]: value } : approval
      )
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="sm:max-w-[1400px] max-h-[95vh] overflow-y-auto modal-content">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Change Order - {mode === 'add' ? 'New' : mode === 'edit' ? 'Edit' : 'View'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'add' ? 'Create a new change order request with detailed impact analysis' : 
             mode === 'edit' ? 'Update change order information and impacts' : 
             'View detailed change order information'}
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="basic">Basic Info</TabsTrigger>
            <TabsTrigger value="impact">Impact Analysis</TabsTrigger>
            <TabsTrigger value="approval">Approval Workflow</TabsTrigger>
            <TabsTrigger value="cost">Cost Breakdown</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="basic" className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Receipt className="h-5 w-5" />
                  Change Order Information
                </h3>
                
                <div className="space-y-2">
                  <Label htmlFor="co-title">Change Order Title *</Label>
                  <Input 
                    id="co-title" 
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Enter change order title"
                    readOnly={isReadOnly}
                  />
                  {errors.title && <p className="text-sm text-destructive">{errors.title}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="co-number">Change Order Number</Label>
                  <Input 
                    id="co-number" 
                    value={formData.coNumber}
                    onChange={(e) => setFormData({...formData, coNumber: e.target.value})}
                    placeholder="Auto-generated if empty"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget">Linked Budget *</Label>
                  <Select value={formData.contract} onValueChange={(value) => {
                    const budget = availableBudgets.find((b: any) => b.budget_id === value);
                    setFormData({...formData, contract: value, project: budget?.project_id || formData.project});
                  }} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select budget" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBudgets.map((b: any) => (
                        <SelectItem key={b.budget_id} value={b.budget_id}>{b.budget_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.contract && <p className="text-sm text-destructive">{errors.contract}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contract_id">Linked Contract</Label>
                  <Select value={formData.contract_id} onValueChange={(value) => setFormData({...formData, contract_id: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select contract (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableContracts.map((c: any) => (
                        <SelectItem key={c.contract_id} value={c.contract_id}>{c.contract_name || c.contract_number || c.contract_id}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea 
                    id="description" 
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Detailed description of the change"
                    rows={4}
                    readOnly={isReadOnly}
                  />
                  {errors.description && <p className="text-sm text-destructive">{errors.description}</p>}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Request Details
                </h3>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="impact-type">Impact Type</Label>
                  <Select value={formData.impactType} onValueChange={(value) => setFormData({...formData, impactType: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {impactTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select value={formData.priority} onValueChange={(value) => setFormData({...formData, priority: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="initiated-by">Initiated By</Label>
                  <Input 
                    id="initiated-by" 
                    value={formData.initiatedBy}
                    onChange={(e) => setFormData({...formData, initiatedBy: e.target.value})}
                    placeholder="Enter person or department"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="request-date">Request Date</Label>
                    <Input 
                      id="request-date" 
                      type="date"
                      value={formData.requestDate}
                      onChange={(e) => setFormData({...formData, requestDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="target-date">Target Date</Label>
                    <Input 
                      id="target-date" 
                      type="date"
                      value={formData.targetDate}
                      onChange={(e) => setFormData({...formData, targetDate: e.target.value})}
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cost-impact">Cost Impact ({orgCurrency}) *</Label>
                    <Input 
                      id="cost-impact" 
                      type="number"
                      value={formData.costImpact}
                      onChange={(e) => setFormData({...formData, costImpact: e.target.value})}
                      placeholder="0.00 (+/- amount)"
                      readOnly={isReadOnly}
                    />
                    {errors.costImpact && <p className="text-sm text-destructive">{errors.costImpact}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="schedule-impact">Schedule Impact (Days)</Label>
                    <Input 
                      id="schedule-impact" 
                      type="number"
                      value={formData.scheduleImpact}
                      onChange={(e) => setFormData({...formData, scheduleImpact: e.target.value})}
                      placeholder="0 (+/- days)"
                      readOnly={isReadOnly}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="justification">Justification</Label>
                  <Textarea 
                    id="justification" 
                    value={formData.justification}
                    onChange={(e) => setFormData({...formData, justification: e.target.value})}
                    placeholder="Reason for the change order"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="impact" className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Comprehensive Impact Analysis
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="risk-assessment">Risk Assessment</Label>
                  <Select value={formData.riskAssessment} onValueChange={(value) => setFormData({...formData, riskAssessment: value})} disabled={isReadOnly}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Critical">Critical</SelectItem>
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quality-impact">Quality Impact</Label>
                  <Textarea 
                    id="quality-impact" 
                    value={formData.qualityImpact}
                    onChange={(e) => setFormData({...formData, qualityImpact: e.target.value})}
                    placeholder="Describe impact on project quality"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="safety-impact">Safety Impact</Label>
                  <Textarea 
                    id="safety-impact" 
                    value={formData.safetyImpact}
                    onChange={(e) => setFormData({...formData, safetyImpact: e.target.value})}
                    placeholder="Describe impact on worker and site safety"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="environmental-impact">Environmental Impact</Label>
                  <Textarea 
                    id="environmental-impact" 
                    value={formData.environmentalImpact}
                    onChange={(e) => setFormData({...formData, environmentalImpact: e.target.value})}
                    placeholder="Describe environmental considerations"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="resource-requirements">Resource Requirements</Label>
                  <Textarea 
                    id="resource-requirements" 
                    value={formData.resourceRequirements}
                    onChange={(e) => setFormData({...formData, resourceRequirements: e.target.value})}
                    placeholder="Detail additional resources needed"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="stakeholder-impact">Stakeholder Impact</Label>
                  <Textarea 
                    id="stakeholder-impact" 
                    value={formData.stakeholderImpact}
                    onChange={(e) => setFormData({...formData, stakeholderImpact: e.target.value})}
                    placeholder="Impact on clients, subcontractors, suppliers"
                    rows={3}
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mitigation-plan">Mitigation Plan</Label>
                  <Textarea 
                    id="mitigation-plan" 
                    value={formData.mitigationPlan}
                    onChange={(e) => setFormData({...formData, mitigationPlan: e.target.value})}
                    placeholder="Plans to mitigate negative impacts"
                    rows={4}
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="approval" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Approval Workflow
              </h3>
            </div>
            
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Approver</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Comments</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {formData.approvalHistory.map(approval => (
                    <TableRow key={approval.id}>
                      <TableCell>
                        <Input 
                          value={approval.approver}
                          onChange={(e) => updateApprovalHistory(approval.id, 'approver', e.target.value)}
                          placeholder="Approver name"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.role}
                          onChange={(e) => updateApprovalHistory(approval.id, 'role', e.target.value)}
                          placeholder="Role"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={approval.required.toString()} 
                          onValueChange={(value) => updateApprovalHistory(approval.id, 'required', value === 'true')}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Required</SelectItem>
                            <SelectItem value="false">Optional</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select 
                          value={approval.status} 
                          onValueChange={(value) => updateApprovalHistory(approval.id, 'status', value)}
                          disabled={isReadOnly}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Pending">Pending</SelectItem>
                            <SelectItem value="Approved">Approved</SelectItem>
                            <SelectItem value="Rejected">Rejected</SelectItem>
                            <SelectItem value="Under Review">Under Review</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input 
                          type="date"
                          value={approval.date}
                          onChange={(e) => updateApprovalHistory(approval.id, 'date', e.target.value)}
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                      <TableCell>
                        <Input 
                          value={approval.comments}
                          onChange={(e) => updateApprovalHistory(approval.id, 'comments', e.target.value)}
                          placeholder="Comments"
                          readOnly={isReadOnly}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="cost" className="space-y-6">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Detailed Cost Breakdown
            </h3>
            
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="labor-hours">Labor Hours</Label>
                  <Input 
                    id="labor-hours" 
                    type="number"
                    value={formData.laborHours}
                    onChange={(e) => setFormData({...formData, laborHours: e.target.value})}
                    placeholder="0"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="material-cost">Material Cost ({orgCurrency})</Label>
                  <Input 
                    id="material-cost" 
                    type="number"
                    value={formData.materialCost}
                    onChange={(e) => setFormData({...formData, materialCost: e.target.value})}
                    placeholder="0.00"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="equipment-cost">Equipment Cost ({orgCurrency})</Label>
                  <Input 
                    id="equipment-cost" 
                    type="number"
                    value={formData.equipmentCost}
                    onChange={(e) => setFormData({...formData, equipmentCost: e.target.value})}
                    placeholder="0.00"
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="overhead-percentage">Overhead Percentage (%)</Label>
                  <Input 
                    id="overhead-percentage" 
                    type="number"
                    value={formData.overheadPercentage}
                    onChange={(e) => setFormData({...formData, overheadPercentage: e.target.value})}
                    placeholder="15"
                    readOnly={isReadOnly}
                  />
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold mb-2">Cost Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Material Cost:</span>
                      <span>{formatCurrency(parseFloat(formData.materialCost || '0'), orgCurrency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Equipment Cost:</span>
                      <span>{formatCurrency(parseFloat(formData.equipmentCost || '0'), orgCurrency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Labor ({formData.laborHours || 0} hrs):</span>
                      <span>{formatCurrency(parseFloat(formData.laborHours || '0') * 75, orgCurrency)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>{formatCurrency(
                        parseFloat(formData.materialCost || '0') +
                        parseFloat(formData.equipmentCost || '0') +
                        (parseFloat(formData.laborHours || '0') * 75),
                        orgCurrency
                      )}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Overhead ({formData.overheadPercentage || 0}%):</span>
                      <span>{formatCurrency(
                        (
                          parseFloat(formData.materialCost || '0') +
                          parseFloat(formData.equipmentCost || '0') +
                          (parseFloat(formData.laborHours || '0') * 75)
                        ) * (parseFloat(formData.overheadPercentage || '0') / 100),
                        orgCurrency
                      )}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Total Cost Impact:</span>
                      <span>{formatCurrency(
                        (parseFloat(formData.materialCost || '0') +
                        parseFloat(formData.equipmentCost || '0') +
                        (parseFloat(formData.laborHours || '0') * 75)) *
                        (1 + parseFloat(formData.overheadPercentage || '0') / 100),
                        orgCurrency
                      )}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Paperclip className="h-5 w-5" />
              Supporting Documents
            </h3>

            {!isReadOnly && (
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => document.getElementById('co-file-upload-input')?.click()}
              >
                <input
                  id="co-file-upload-input"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  disabled={isUploading}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.heic,.xls,.xlsx"
                />
                <div className="flex flex-col items-center gap-2">
                  {isUploading ? (
                    <>
                      <Upload className="h-10 w-10 text-primary animate-pulse" />
                      <p className="text-sm font-medium text-primary">Uploading files...</p>
                      <p className="text-xs text-muted-foreground">Please wait</p>
                    </>
                  ) : (
                    <>
                      <Upload className="h-10 w-10 text-gray-400" />
                      <p className="text-sm font-medium">Click to upload or drag and drop</p>
                      <p className="text-xs text-muted-foreground">
                        PDF, Word, Excel, Images (Max 10MB per file)
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            {uploadedFiles.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Uploaded Files ({uploadedFiles.length})
                </p>
                <div className="border rounded-md divide-y max-h-64 overflow-y-auto">
                  {uploadedFiles.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      {!isReadOnly && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFile(file.id)}
                          className="flex-shrink-0"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Paperclip className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No documents uploaded yet</p>
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          {!isReadOnly && (
            <>
              <Button variant="outline">
                Save as Draft
              </Button>
              <Button onClick={handleSave} disabled={isSubmitting}>
                {isSubmitting ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {mode === 'add' ? 'Submit Change Order' : 'Update Change Order'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ChangeOrders() {
  const { canWrite, canApprove, canDelete } = usePermission();
  const { orgId } = useAuth();
  const orgCurrency = useOrgCurrency();
  const { navigate, routeFilters } = useAppNav();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [budgetFilter, setBudgetFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [viewingCO, setViewingCO] = useState<any | null>(null);
  const [editingCO, setEditingCO] = useState<any | null>(null);

  const { data: rawCOs = [], isLoading: loading, refetch } = useChangeOrders(orgId);
  const { data: rawBudgets = [] } = useBudgets(orgId);
  const { data: rawContracts = [] } = useContracts(orgId);
  const budgets = rawBudgets as any[];
  const contracts = rawContracts as any[];

  // Apply incoming route filters on first render
  useEffect(() => {
    if (routeFilters.budget_id) setBudgetFilter(routeFilters.budget_id);
    if (routeFilters.contract_id) {
      // No separate contract filter UI yet — pre-filter via search
    }
  }, []); // intentional empty dep — apply once on navigation

  const handleApproveChangeOrder = async (id: string) => {
    try {
      await financialService.approveBudgetChangeOrder(id);
      showSuccess('Change order approved');
      refetch();
    } catch {
      showError('Failed to approve change order');
    }
  };

  const changeOrders = (rawCOs as any[]).map(co => ({
    id: co.change_order_id,
    coNumber: co.change_order_number || '',
    title: co.title || '',
    contract: co.budget_id || '',
    contract_id: co.contract_id || '',
    project: co.project_id || '',
    category: co.change_type || '',
    impactType: co.impact_type || '',
    description: co.description || '',
    costImpact: co.cost_impact ?? 0,
    scheduleImpact: co.schedule_impact_days ?? 0,
    priority: co.priority || 'Medium',
    status: co.status || '',
    requestDate: co.request_date || '',
    targetDate: co.required_decision_date || '',
    completion: co.status === 'executed' || co.status === 'completed' ? 100 : co.status === 'approved' ? 50 : 0,
    estimatedCost: co.cost_impact ?? 0,
    actualCost: 0,
    laborHours: 0,
    materialCost: 0,
    equipmentCost: 0,
    overheadPercentage: 15,
    documentsCount: 0,
    initiatedBy: co.created_by_user_id || '',
    lastUpdated: co.updated_at || '',
    _raw: co,
  }));

  const filteredChangeOrders = useMemo(() => {
    return changeOrders.filter((co: typeof changeOrders[0]) => {
      const matchesSearch = co.coNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          co.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          co.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || co.status.toLowerCase().replace(/\s+/g, '') === statusFilter;
      const matchesBudget = budgetFilter === 'all' || co.contract === budgetFilter;
      const matchesPriority = priorityFilter === 'all' || co.priority.toLowerCase() === priorityFilter;

      return matchesSearch && matchesStatus && matchesBudget && matchesPriority;
    });
  }, [searchTerm, statusFilter, budgetFilter, priorityFilter, changeOrders]);

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase().replace(' ', '')) {
      case 'pendingapproval':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Pending Approval</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Approved</Badge>;
      case 'inprogress':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">In Progress</Badge>;
      case 'completed':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Completed</Badge>;
      case 'underreview':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200">Under Review</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Critical</Badge>;
      case 'high':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200">High</Badge>;
      case 'medium':
        return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">Medium</Badge>;
      case 'low':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Low</Badge>;
      default:
        return <Badge variant="secondary">{priority}</Badge>;
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBudgetFilter('all');
    setPriorityFilter('all');
  };

  const stats = useMemo(() => {
    const pending = changeOrders.filter((co) => ['pending', 'submitted', 'draft', 'under review'].includes(co.status.toLowerCase())).length;
    const totalCostImpact = changeOrders.reduce((sum, co) => sum + co.costImpact, 0);
    const avgScheduleImpact = changeOrders.length > 0 ? changeOrders.reduce((sum, co) => sum + co.scheduleImpact, 0) / changeOrders.length : 0;
    const completedRate = changeOrders.length > 0 ? (changeOrders.filter((co) => co.status === 'executed' || co.status === 'completed').length / changeOrders.length) * 100 : 0;
    return { pending, totalCostImpact, avgScheduleImpact, completedRate };
  }, [changeOrders]);

  const getBudgetName = (budgetId: string) => budgets.find((b: any) => b.budget_id === budgetId)?.budget_name || budgetId;
  const getContractName = (contractId: string) => contracts.find((c: any) => c.contract_id === contractId)?.contract_name || contracts.find((c: any) => c.contract_id === contractId)?.contract_number || contractId;

  return (
    <div className="space-y-6">
      {/* Enhanced Header */}
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div>
          <h2 className="text-3xl font-semibold tracking-tight">Change Order Management</h2>
          <p className="text-muted-foreground">Track and manage contract change orders with comprehensive impact analysis</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {canWrite('/change-orders') && (
            <ChangeOrderFormModal
              mode="add"
              orgId={orgId ?? ''}
              availableBudgets={budgets}
              availableContracts={contracts}
              onSuccess={() => refetch()}
              trigger={
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Change Order
                </Button>
              }
            />
          )}
          <Button variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-700">{stats.pending}</div>
            <p className="text-xs text-yellow-600 mt-1">Awaiting decision</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-green-600" />
              Total Cost Impact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.totalCostImpact >= 0 ? 'text-red-700' : 'text-green-700'}`}>
              {stats.totalCostImpact >= 0 ? '+' : ''}${(stats.totalCostImpact / 1000).toFixed(0)}K
            </div>
            <p className="text-xs text-green-600 mt-1">Across all orders</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4 text-blue-600" />
              Avg Schedule Impact
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${stats.avgScheduleImpact >= 0 ? 'text-red-700' : 'text-green-700'}`}>
              {stats.avgScheduleImpact >= 0 ? '+' : ''}{stats.avgScheduleImpact.toFixed(0)} days
            </div>
            <p className="text-xs text-blue-600 mt-1">Average per order</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-purple-600" />
              Completion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-700">{stats.completedRate.toFixed(0)}%</div>
            <p className="text-xs text-purple-600 mt-1">Successfully completed</p>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Change Orders Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Change Order Directory
          </CardTitle>
          <CardDescription>
            Comprehensive change order tracking with impact analysis
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Change Order Details</TableHead>
                  <TableHead>Project & Contract</TableHead>
                  <TableHead>Impact Analysis</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredChangeOrders.map(co => (
                  <TableRow key={co.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="space-y-1">
                        <p className="font-medium">{co.coNumber}</p>
                        <p className="text-sm">{co.title}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">{co.category}</Badge>
                          {getPriorityBadge(co.priority)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        {co.contract && (
                          <div className="font-medium">{getBudgetName(co.contract)}</div>
                        )}
                        {co.contract_id && (
                          <div className="text-muted-foreground text-xs">{getContractName(co.contract_id)}</div>
                        )}
                        {co.initiatedBy && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Users className="h-3 w-3" />
                            <span>{co.initiatedBy}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>{co.requestDate}</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2">
                          <DollarSign className="h-3 w-3" />
                          <span className={`font-medium ${co.costImpact >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {co.costImpact >= 0 ? '+' : ''}{formatCurrency(co.costImpact, orgCurrency)}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span className={`${co.scheduleImpact >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {co.scheduleImpact >= 0 ? '+' : ''}{co.scheduleImpact} days
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {co.impactType}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{co.completion}%</span>
                        </div>
                        <Progress value={co.completion} className="h-2" />
                        <div className="text-xs text-muted-foreground">
                          Target: {co.targetDate}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {co.documentsCount} documents
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(co.status)}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onSelect={() => setViewingCO(co)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {canWrite('/change-orders') && (
                            <DropdownMenuItem onSelect={() => setEditingCO(co)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Change Order
                            </DropdownMenuItem>
                          )}
                          {co.contract && (
                            <DropdownMenuItem onSelect={() => navigate('/budget-cost', { budget_id: co.contract })}>
                              <DollarSign className="h-4 w-4 mr-2" />
                              View Budget
                            </DropdownMenuItem>
                          )}
                          {co.contract_id && (
                            <DropdownMenuItem onSelect={() => navigate('/contracts', { contract_id: co.contract_id })}>
                              <Receipt className="h-4 w-4 mr-2" />
                              View Contract
                            </DropdownMenuItem>
                          )}
                          {canApprove('/change-orders') && co._raw && (
                            <DropdownMenuItem onSelect={() => handleApproveChangeOrder(co._raw.change_order_id)}>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve
                            </DropdownMenuItem>
                          )}
                          {canDelete('/change-orders') && (
                            <DropdownMenuItem className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {filteredChangeOrders.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>{changeOrders.length === 0 ? 'No change orders yet. Create your first change order.' : 'No change orders match your current filters.'}</p>
                {changeOrders.length > 0 && (
                  <Button variant="outline" size="sm" onClick={resetFilters} className="mt-2">
                    Clear Filters
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {viewingCO && (
        <ChangeOrderFormModal
          mode="view"
          changeOrder={viewingCO}
          availableBudgets={budgets}
          availableContracts={contracts}
          open={true}
          onOpenChange={(o) => { if (!o) setViewingCO(null); }}
        />
      )}
      {editingCO && (
        <ChangeOrderFormModal
          mode="edit"
          changeOrder={editingCO}
          orgId={orgId ?? ''}
          availableBudgets={budgets}
          availableContracts={contracts}
          onSuccess={() => { refetch(); setEditingCO(null); }}
          open={true}
          onOpenChange={(o) => { if (!o) setEditingCO(null); }}
        />
      )}
    </div>
  );
}